import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { StorageService, View } from '@lib/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Demand } from '../../../types/Demand';
import { DemandProduct } from '../../../types/DemandProduct';
import { CreateDemandProductService } from '../../../create/create-demand-product.service';
import { HttpStatusCode } from '@angular/common/http';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractDemandDialogComponent } from '../AbstractDemandDialogComponent';
import { ClientDataService, ICustomerCompanySettings } from '@lib/client';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { PurchaseRequisitionResult } from '../../../../purchase-requisition/types/PurchaseRequisitionResult';
import { firstValueFrom } from 'rxjs';
import { EprocUser, UserService } from '@lib/user';
import { DisplayCurrency, SortingService } from '@lib/util';
import { I18nService } from '@lib/i18n';
import { DemandProductPriceInputComponent } from '../add-product/add-product-price/input/demand-product-price-input.component';
import { DemandProductDeliveryInputComponent } from '../add-product/add-product-delivery/input/demand-product-delivery-input.component';
import { DemandAddProductDataInputComponent } from '../add-product/add-product-data/input/demand-add-product-data-input.component';
import { DemandSelectableCurrencies } from '../../../types/IDemandSelectableCurrencies';

export class DemandEditProductDialogData {
    constructor(
        public demand: Demand,
        public readonly resultsIn: PurchaseRequisitionResult,
        public productId: string,
    ) {}
}

export const RESULTS_IN = 'resultsIn';

@Component({
    selector: 'demand-edit-product',
    templateUrl: './demand-edit-product.component.html',
    styleUrls: ['./demand-edit-product.component.scss'],
    standalone: false,
})
export class DemandEditProductComponent
    extends AbstractDemandDialogComponent<DemandEditProductDialogData>
    implements OnInit, OnDestroy
{
    private readonly storage = inject(StorageService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly router = inject(Router);
    private readonly clientDataService = inject(ClientDataService);
    private readonly userService = inject(UserService);
    private readonly i18n = inject(I18nService);

    @ViewChild(DemandProductPriceInputComponent) priceComponent?: DemandProductPriceInputComponent;
    @ViewChild(DemandAddProductDataInputComponent) dataComponent?: DemandAddProductDataInputComponent;
    @ViewChild(DemandProductDeliveryInputComponent) deliveryComponent?: DemandProductDeliveryInputComponent;

    public product!: DemandProduct;
    public View = View;

    public dataGroup!: UntypedFormGroup;
    public deliveryGroup!: UntypedFormGroup;
    public priceGroup!: UntypedFormGroup;

    public error?: HttpStatusCode;

    public commentCtrl = new UntypedFormControl();
    public internalCommentCtrl = new UntypedFormControl('', Validators.maxLength(250));
    public defaultArticleNumber!: string;
    public deliveryDateRequired!: boolean;
    public supplierArticleNumberRequired!: boolean;

    public activeCurrencies?: DisplayCurrency[];
    /** ISO codes the customer does not allow (EPROC-11425), hidden from the dropdown. */
    public customerExcludedCurrencies = new Set<string>();
    /** ISO codes the customer allows but a supplier of the demand does not accept (EPROC-11425). */
    public supplierExcludedCurrencies = new Set<string>();

    public demand!: Demand;

    private user!: EprocUser;

    public get backBtnText(): string {
        return this.demand.isAnnounced ? marker('zurück zur Bedarfsmeldung') : marker('zurück zum Bedarf');
    }

    private get dialogData(): DemandEditProductDialogData | undefined {
        if (this.insideDialog) {
            return this.injector.get<DemandEditProductDialogData>(MAT_DIALOG_DATA);
        }

        return undefined;
    }

    private get productId(): string {
        if (this.insideDialog && this.dialogData) {
            return this.dialogData.productId;
        }

        return this.route.snapshot.params['productId'];
    }

    private get resultsIn(): PurchaseRequisitionResult {
        if (this.insideDialog && this.dialogData?.resultsIn) {
            return this.dialogData.resultsIn;
        }
        if (this.route.snapshot.queryParamMap.has(RESULTS_IN)) {
            return this.route.snapshot.queryParamMap.get(RESULTS_IN) as PurchaseRequisitionResult;
        }

        return PurchaseRequisitionResult.RFQ;
    }

    public getDemand(): Promise<Demand> {
        if (this.insideDialog && this.dialogData) {
            return Promise.resolve(this.dialogData.demand);
        }

        const demandId: string = this.route.snapshot.params['demandId'];
        return Demand.byId(
            demandId,
            this.dataService,
            this.commentDataService,
            this.itemClassificationDataService,
            this.fileDataService,
            this.i18n,
        );
    }

    /**
     * Loads the currency dropdown and the exclusions behind its hints
     * ({@link DemandSelectableCurrencies.exclusionsAmong}, EPROC-11425). Requires the demand to be
     * loaded: the selectable currencies depend on its suppliers.
     */
    private async getCurrencies(): Promise<void> {
        const user: EprocUser = await this.userService.getMyself();
        const [activeIsos, selectable] = await Promise.all([
            firstValueFrom(this.clientDataService.getActiveCurrencies()),
            firstValueFrom(this.dataService.getSelectableCurrencies(this.demand.id)).catch(
                (): DemandSelectableCurrencies | undefined => undefined,
            ),
        ]);
        this.activeCurrencies = activeIsos.map((iso) => new DisplayCurrency(iso, user.language.iso639LanguageAlpha2));
        SortingService.sortSortables(this.activeCurrencies);
        const exclusions = DemandSelectableCurrencies.exclusionsAmong(activeIsos, selectable);
        this.customerExcludedCurrencies = exclusions.byCustomer;
        this.supplierExcludedCurrencies = exclusions.bySupplier;
    }

    async ngOnInit(): Promise<void> {
        try {
            // The demand has to be loaded first: the selectable currencies depend on its suppliers.
            this.demand = await this.getDemand();
            await this.getCurrencies();
            this.user = await this.userService.getMyself();
            const settings: ICustomerCompanySettings = await firstValueFrom(this.clientDataService.getSettings());
            this.defaultArticleNumber = settings.rfq.defaultArticleNumber;
            this.deliveryDateRequired = settings.deliveryDateRequired;
            this.supplierArticleNumberRequired = settings.supplierArticleNumberRequired;
            this.product = this.demand.getProduct(this.productId) as DemandProduct;
            if (!this.demand.readOnly) {
                this.initForms();
            } else {
                this.error = HttpStatusCode.Forbidden;
            }
        } catch {
            this.error = HttpStatusCode.NotFound;
        }
    }

    public ngOnDestroy(): void {
        this.closeDialog(this.demand);
    }

    private initForms(): void {
        const service: CreateDemandProductService = new CreateDemandProductService();
        this.dataGroup = service.initDataFormGroup(
            this.formBuilder,
            this.product,
            this.demand,
            this.supplierArticleNumberRequired,
            this.defaultArticleNumber,
        );
        this.priceGroup = service.initPriceFormGroup(this.formBuilder, this.product, this.user);
        this.deliveryGroup = service.initDeliveryFormGroup(this.formBuilder, this.product, this.deliveryDateRequired);
        this.commentCtrl.setValue(this.product.comment.comment);
        this.internalCommentCtrl.setValue(this.product.comment.internalComment);
    }

    public async save(): Promise<void> {
        if (this.dataGroup.valid && this.priceGroup.valid && this.deliveryGroup.valid) {
            const service: CreateDemandProductService = new CreateDemandProductService();
            service.insertDataIntoProduct(
                this.product,
                this.dataGroup,
                this.priceGroup,
                this.deliveryGroup,
                this.commentCtrl.value,
                this.internalCommentCtrl.value,
            );
            await this.product.save();
            if (this.insideDialog) {
                this.closeDialog(this.demand);
            } else {
                await this.router.navigate([View.CLIENT, 'rfq', 'demand', this.demand.id]);
            }
        }
    }
}
